import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, Plus, Search, Filter, Edit, Eye, UserPlus, Calendar, TrendingUp } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CasesManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await api.get('/cases');
      setCases(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل القضايا' : 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي القضايا' : 'Total Cases',
      value: cases.length,
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'القضايا النشطة' : 'Active',
      value: cases.filter(c => c.status === 'active').length,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending',
      value: cases.filter(c => c.status === 'pending').length,
      icon: Calendar,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: i18n.language === 'ar' ? 'مغلقة' : 'Closed',
      value: cases.filter(c => c.status === 'closed').length,
      icon: Briefcase,
      color: 'from-gray-500 to-gray-600'
    },
  ];

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         c.case_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">{i18n.language === 'ar' ? 'إدارة القضايا' : 'Cases Management'}</h1>
              <p className="text-text-secondary text-lg">{i18n.language === 'ar' ? 'مراقبة وإدارة جميع القضايا' : 'Monitor and manage all cases'}</p>
            </div>
            <Button className="btn-primary gap-2" onClick={() => navigate('/admin/cases/new')}>
              <Plus className="w-5 h-5" />
              {i18n.language === 'ar' ? 'قضية جديدة' : 'New Case'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input
                    placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{i18n.language === 'ar' ? 'الكل' : 'All'}</SelectItem>
                    <SelectItem value="pending">{i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending'}</SelectItem>
                    <SelectItem value="active">{i18n.language === 'ar' ? 'نشطة' : 'Active'}</SelectItem>
                    <SelectItem value="closed">{i18n.language === 'ar' ? 'مغلقة' : 'Closed'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredCases.length === 0 ? (
                <div className="text-center py-20">
                  <Briefcase className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                  <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد قضايا' : 'No cases found'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCases.map((caseItem) => (
                    <div key={caseItem.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: '#9D7E4F' }}>
                          <Briefcase className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <p className="font-semibold text-lg">{caseItem.title}</p>
                            <Badge variant={caseItem.status === 'active' ? 'default' : 'secondary'}>
                              {i18n.language === 'ar'
                                ? (caseItem.status === 'active' ? 'نشطة' : caseItem.status === 'pending' ? 'قيد الانتظار' : 'مغلقة')
                                : caseItem.status
                              }
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-text-secondary">
                            <span>{caseItem.case_number}</span>
                            <span>•</span>
                            <span>{format(new Date(caseItem.created_at), 'dd/MM/yyyy')}</span>
                            {caseItem.department && (
                              <>
                                <span>•</span>
                                <span>{caseItem.department}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/cases/${caseItem.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <UserPlus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CasesManagement;